import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import EventsFormDialog from "./EventsFormDialog";
import { useEventAuditLogApi } from "../hooks/useEventAuditLogApi";
import type { EventAuditLogRow } from "../shared/eventAuditLog";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";

function actionColor(action: EventAuditLogRow["action"]): "default" | "primary" | "success" | "warning" | "error" {
  switch (action) {
    case "create":
      return "primary";
    case "update":
      return "default";
    case "delete":
      return "error";
    case "approve":
      return "success";
    default:
      return "default";
  }
}

type AdminAuditLogPanelProps = {
  /** When false, no API calls (accordion collapsed). */
  active: boolean;
};

const AdminAuditLogPanel: React.FC<AdminAuditLogPanelProps> = ({ active }) => {
  const { fetchAuditLogPage } = useEventAuditLogApi();
  const [rows, setRows] = useState<EventAuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editEventId, setEditEventId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAuditLogPage(page + 1, rowsPerPage);
      setRows(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setError(err.responseBody || err.message || "Failed to load audit log.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchAuditLogPage, page, rowsPerPage]);

  useEffect(() => {
    if (!active) return;
    void load();
  }, [active, load]);

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Create, update, delete, and approve actions on events (API). Actor is taken from the access token
          (user name / application client id). Times are shown in your local timezone.
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => void load()} disabled={loading} size="small" aria-label="Refresh audit log">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : null}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading || rows.length > 0 ? (
        <>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Event Code</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatLocalDateTime(r.created_at)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.action} color={actionColor(r.action)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.actor_display_name || r.actor_subject || "—"}
                      </Typography>
                      {r.actor_subject && r.actor_display_name !== r.actor_subject ? (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap title={r.actor_subject}>
                          {r.actor_subject}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {r.global_event_id != null ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setEditEventId(r.global_event_id)}
                          sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                        >
                          {r.global_event_id}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{r.event_code ?? "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.event_type}>
                      {r.event_type || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 15, 25, 50]}
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      ) : null}

      <EventsFormDialog
        open={editEventId != null}
        onClose={() => {
          setEditEventId(null);
          if (active) void load();
        }}
        onSaved={() => {
          if (active) void load();
        }}
        eventId={editEventId}
        createNewEvent={false}
        onNavigateToEvent={(id) => setEditEventId(id)}
      />
    </Box>
  );
};

export default AdminAuditLogPanel;
